import { useEffect, useState } from "react";
import { Download, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { downloadInvoicePdf } from "../../lib/download";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { formatPrice } from "../../lib/utils";

const FILTERS = [
  { value: "unpaid", label: "Unpaid" },
  { value: "overdue", label: "Overdue" },
  { value: "paid", label: "Paid" },
  { value: "void", label: "Void" },
  { value: "", label: "All" },
];

const TYPES = [
  { value: "", label: "All types" },
  { value: "subscription", label: "SHUWA Pro" },
  { value: "statement", label: "Statements" },
];

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

/**
 * Who owes SHUWA what: every SHUWA Pro invoice and monthly statement, with
 * how late it is, a PDF, and offline (cash / bank transfer) settlement.
 */
export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("unpaid");
  const [type, setType] = useState("");
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    api
      .get("/admin/invoices", { params: { status: status || undefined, type: type || undefined, q: query || undefined } })
      .then(({ data }) => {
        setInvoices(data.data || []);
        setTotals(data.totals);
      })
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, type]);

  const update = async (invoice, next) => {
    const question =
      next === "paid"
        ? invoice.type === "subscription"
          ? `Record ${invoice.number} as paid (cash / bank transfer)? This also renews SHUWA Pro for ${invoice.business?.name}.`
          : `Record ${invoice.number} as paid (cash / bank transfer)?`
        : `Void ${invoice.number}? ${invoice.business?.name} will no longer owe it.`;
    if (!window.confirm(question)) return;
    setBusyId(invoice.id);
    try {
      await api.patch(`/admin/invoices/${invoice.id}`, { status: next });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <h1 className="mb-4 font-montserrat text-2xl font-bold text-slate-900">Invoices</h1>

      {totals && (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Unpaid ({totals.unpaid_count})</p>
            <p className="font-montserrat text-xl font-bold text-slate-900">{formatPrice(totals.unpaid)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Overdue ({totals.overdue_count})</p>
            <p className="font-montserrat text-xl font-bold text-amber-700">{formatPrice(totals.overdue)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Shops hidden for non-payment</p>
            <p className="font-montserrat text-xl font-bold text-slate-900">{totals.businesses_on_hold}</p>
          </Card>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatus(f.value)}
            className={`rounded-full border px-3 py-1.5 text-sm ${status === f.value ? "border-primary bg-primary text-white" : "border-border"}`}
          >
            {f.label}
          </button>
        ))}
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          aria-label="Invoice type"
          className="rounded-full border border-border bg-white px-3 py-1.5 text-sm"
        >
          {TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
          className="ml-auto w-full sm:w-64"
        >
          <Input placeholder="Search number or business" aria-label="Search invoices" value={query} onChange={(e) => setQuery(e.target.value)} />
        </form>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-3">
          {invoices.map((invoice) => {
            const busy = busyId === invoice.id;
            const open = invoice.status === "open";
            return (
              <Card key={invoice.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-semibold text-slate-900">
                    {invoice.business?.name || "Deleted business"}
                    <span className="text-xs font-normal text-muted-foreground">{invoice.number}</span>
                  </p>
                  <p className="break-words text-xs text-muted-foreground">
                    {invoice.business?.owner?.full_name} · {invoice.business?.owner?.email}
                    {invoice.business?.owner?.phone ? ` · ${invoice.business.owner.phone}` : ""}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant="default">{invoice.type === "subscription" ? "SHUWA Pro" : "Statement"}</Badge>
                    {invoice.status === "paid" && <Badge variant="accent">Paid {formatDate(invoice.paid_at)}</Badge>}
                    {invoice.status === "void" && <Badge variant="dark">Void</Badge>}
                    {open &&
                      (invoice.days_overdue > 0 ? (
                        <Badge variant="warning">
                          {invoice.days_overdue} day{invoice.days_overdue === 1 ? "" : "s"} overdue
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">Due {formatDate(invoice.due_at)}</span>
                      ))}
                    {invoice.business?.billing_hold_at && (
                      <span className="flex items-center gap-1 font-medium text-amber-700">
                        <EyeOff className="h-3.5 w-3.5" aria-hidden="true" /> Shop hidden
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="mr-2 font-montserrat text-lg font-bold text-slate-900">{formatPrice(invoice.amount)}</span>
                  <Button size="sm" variant="outline" onClick={() => downloadInvoicePdf(invoice, "admin")}>
                    <Download className="h-4 w-4" /> PDF
                  </Button>
                  {open && (
                    <>
                      <Button size="sm" disabled={busy} onClick={() => update(invoice, "paid")}>
                        Mark paid
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => update(invoice, "void")}>
                        Void
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            );
          })}
          {invoices.length === 0 && <p className="text-muted-foreground">No invoices found.</p>}
        </div>
      )}
    </div>
  );
}
